import { Bot, MessageSquareQuote, PencilLine, Puzzle } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useAnchoredMenuPosition } from "@/hooks/use-anchored-menu-position";
import { useScrollActiveIntoView } from "@/hooks/use-scroll-active-into-view";
import { cn } from "@/lib/utils";

import {
  SLASH_COMMAND_OPTIONS,
  SUGGESTED_SLASH_COMMENTS,
} from "./slash-command-data";
import type {
  SlashAgentOption,
  SlashCommandOption,
  SlashMenuMode,
  SlashSkillOption,
} from "./slash-command-data";

/**
 * Props for the floating slash command menu.
 * Keeps keyboard handling in the textarea while rendering a scoped picker above it.
 */
interface SlashCommandMenuProps {
  /**
   * Whether the menu should be visible.
   * The parent controls this from the textarea value and interaction state.
   */
  open: boolean;

  /**
   * Active menu mode controlling which result set to render.
   * Root lists commands while nested modes list agents or comment suggestions.
   */
  mode: SlashMenuMode;

  /**
   * Search string extracted from the textarea after the slash command.
   * Used to filter visible items without moving focus away from the textarea.
   */
  query: string;

  /**
   * Anchor element used to position the menu relative to the composer.
   * The textarea is the current anchor in the chat input.
   */
  anchorElement: HTMLElement | null;

  /**
   * Preferred side of the anchor for the menu. Defaults to "up" (above the
   * composer), which suits the messages view where the input sits at the
   * bottom. The new-chat surfaces center the input with room below, so they
   * pass "down". This is only a preference — the menu flips to the opposite
   * side and clamps to the viewport when the preferred side can't fit.
   */
  placement?: "up" | "down";

  /**
   * Agent results to show for the nested agent menu.
   * Real agents can be passed through directly, with dummy options as fallback.
   */
  agentOptions: SlashAgentOption[];

  /**
   * Loading flag for live agent fetching.
   * Shows a lightweight spinner row while the picker warms up.
   */
  agentsLoading?: boolean;

  /**
   * Loading flag for live skill fetching.
   * Shows a lightweight spinner row while the picker warms up.
   */
  skillsLoading?: boolean;

  /**
   * Skill results to show in the dollar-triggered skill palette.
   * Empty list means no Skills section is rendered.
   */
  skillOptions?: SlashSkillOption[];

  /**
   * Controls menu dismissal from the parent.
   * Used for escape and outside-click handling.
   */
  onOpenChange: (open: boolean) => void;

  /**
   * Callback when a root command is selected.
   * Commands can open nested modes or inject starter text.
   */
  onCommandSelect: (command: SlashCommandOption) => void;

  /**
   * Callback when an agent row is selected.
   * The parent decides whether to route to a real agent or keep it as a preview.
   */
  onAgentSelect: (agent: SlashAgentOption) => void;

  /**
   * Show the "Agent" entry in the root slash menu. Defaults to true.
   * Set false in direct sub-agent chats — picking a different specialist mid
   * conversation only makes sense in a Super-Agent (router) context.
   */
  showAgentCommand?: boolean;

  /**
   * Callback when a skill row is selected. The parent inserts a skill
   * token into the composer so the runtime can resolve and load it.
   */
  onSkillSelect?: (skill: SlashSkillOption) => void;

  /**
   * Callback when a note suggestion or custom note is selected.
   * The parent converts the selected value into a removable composer chip.
   */
  onCommentSelect: (comment: string) => void;
}

type SlashMenuItem =
  | { id: string; kind: "command"; label: string; description: string; icon: SlashCommandOption["icon"]; command: SlashCommandOption }
  | { id: string; kind: "agent"; label: string; description: string; icon: typeof Bot; agent: SlashAgentOption }
  | { id: string; kind: "skill"; label: string; description: string; icon: typeof Puzzle; skill: SlashSkillOption }
  | { id: string; kind: "comment"; label: string; description: string; icon: typeof MessageSquareQuote; comment: string }
  | { id: string; kind: "create-comment"; label: string; description: string; icon: typeof PencilLine; comment: string };

/**
 * SlashCommandMenu renders a floating palette above the textarea.
 * The textarea keeps focus, so keyboard navigation is handled manually.
 */
export function SlashCommandMenu({
  open,
  mode,
  query,
  anchorElement,
  placement = "up",
  agentOptions,
  agentsLoading = false,
  skillsLoading = false,
  skillOptions = [],
  onOpenChange,
  onCommandSelect,
  onAgentSelect,
  showAgentCommand = true,
  onSkillSelect,
  onCommentSelect,
}: SlashCommandMenuProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  /**
   * Portal mounting flag for client-only rendering.
   * Prevents document access during hydration.
   */
  const [mounted, setMounted] = useState(false);

  /**
   * Keyboard-selected row index.
   * Since the textarea stays focused, we track highlight state locally.
   */
  const [selectedIndex, setSelectedIndex] = useState(0);

  /**
   * Collision-aware floating position. Opens on `placement` but flips and
   * clamps to the viewport so the menu is never pushed off-screen.
   */
  const position = useAnchoredMenuPosition({
    anchorElement,
    menuRef: dropdownRef,
    open,
    preferredSide: placement,
  });

  /**
   * Items split into rendered sections. Root mode shows slash patterns,
   * while scoped modes show one result type. Keyboard nav
   * uses the flat `items` array, so the global index maps to whichever
   * section the cursor lands in.
   */
  const sections = useMemo<{ heading?: string; items: SlashMenuItem[] }[]>(() => {
    const normalizedQuery = query.toLowerCase();

    if (mode === "agents") {
      const agentItems: SlashMenuItem[] = agentOptions
        .filter((agent) => {
          if (!normalizedQuery) return true;
          return (
            agent.name.toLowerCase().includes(normalizedQuery)
            || agent.role.toLowerCase().includes(normalizedQuery)
          );
        })
        .map((agent) => ({
          id: agent.id,
          kind: "agent" as const,
          label: agent.name,
          description: agent.role,
          icon: Bot,
          agent,
        }));

      return [{ items: agentItems }];
    }

    if (mode === "skills") {
      const skillItems: SlashMenuItem[] = skillOptions
        .filter((skill) => {
          if (!normalizedQuery) return true;
          return (
            skill.name.toLowerCase().includes(normalizedQuery)
            || skill.description.toLowerCase().includes(normalizedQuery)
            || skill.slug.toLowerCase().includes(normalizedQuery)
          );
        })
        .map((skill) => ({
          id: `skill-${skill.id}`,
          kind: "skill" as const,
          label: skill.name,
          description: skill.description,
          icon: Puzzle,
          skill,
        }));

      return [{ items: skillItems }];
    }

    if (mode === "comments") {
      const filteredSuggestions = SUGGESTED_SLASH_COMMENTS.filter((comment) => {
        if (!normalizedQuery) return true;
        return comment.toLowerCase().includes(normalizedQuery);
      });

      const commentItems: SlashMenuItem[] = filteredSuggestions.map((comment) => ({
        id: comment,
        kind: "comment",
        label: comment,
        description: "Add this note to the current prompt",
        icon: MessageSquareQuote,
        comment,
      }));

      if (query) {
        commentItems.unshift({
          id: `custom-${query}`,
          kind: "create-comment",
          label: `Add "${query}"`,
          description: "Create a custom note from the text you typed",
          icon: PencilLine,
          comment: query,
        });
      }

      return [{ items: commentItems }];
    }

    const commandItems: SlashMenuItem[] = SLASH_COMMAND_OPTIONS.filter((command) => {
      // Outside a Super-Agent context the agent-picker entry is irrelevant —
      // the user is already on a specific specialist and would just be
      // confused by an option that does nothing meaningful (issue #981).
      if (command.id === "agent" && !showAgentCommand) return false;
      if (!normalizedQuery) return true;
      return (
        command.label.includes(normalizedQuery)
        || command.title.toLowerCase().includes(normalizedQuery)
        || command.description.toLowerCase().includes(normalizedQuery)
      );
    }).map((command) => ({
      id: command.id,
      kind: "command" as const,
      label: command.title,
      description: command.description,
      icon: command.icon,
      command,
    }));

    return [{ items: commandItems }];
  }, [agentOptions, mode, query, skillOptions, showAgentCommand]);

  /** Flat list used for keyboard navigation across all rendered sections. */
  const items = useMemo<SlashMenuItem[]>(
    () => sections.flatMap((section) => section.items),
    [sections],
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items, mode, query]);

  const handleSelect = useCallback((item: SlashMenuItem) => {
    if (item.kind === "command") {
      onCommandSelect(item.command);
      return;
    }

    if (item.kind === "agent") {
      onAgentSelect(item.agent);
      return;
    }

    if (item.kind === "skill") {
      onSkillSelect?.(item.skill);
      return;
    }

    onCommentSelect(item.comment);
  }, [onAgentSelect, onCommandSelect, onCommentSelect, onSkillSelect]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex((previous) => Math.min(previous + 1, Math.max(items.length - 1, 0)));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex((previous) => Math.max(previous - 1, 0));
      } else if (event.key === "Enter" && items[selectedIndex]) {
        event.preventDefault();
        event.stopPropagation();
        handleSelect(items[selectedIndex]);
      } else if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onOpenChange(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [handleSelect, items, onOpenChange, open, selectedIndex]);

  // Keep the keyboard-focused row visible as the user arrows through the list.
  useScrollActiveIntoView(listRef, selectedIndex, open);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current
        && !dropdownRef.current.contains(event.target as Node)
        && anchorElement
        && !anchorElement.contains(event.target as Node)
      ) {
        onOpenChange(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [anchorElement, onOpenChange, open]);

  if (!open || !mounted) {
    return null;
  }

  const emptyCopy = mode === "root"
    ? "No patterns found."
    : mode === "agents"
      ? "No agents found."
      : mode === "skills"
        ? "No skills found."
        : mode === "comments"
          ? "No matching notes found."
          : "No commands found.";

  // Value of the keyboard-selected row, used to drive both the highlight
  // (Command `value`) and the scroll marker (`data-active`) below.
  const activeId = items[selectedIndex]?.id ?? "";

  const dropdown = (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] w-[320px] overflow-hidden rounded-lg border border-gray-200 bg-popover text-popover-foreground shadow-lg shadow-gray-200/60"
      style={{ top: position.top, left: position.left }}
    >
      <Command
        className="rounded-lg border-none bg-transparent"
        // Keep cmdk's own selection in lockstep with our manual selectedIndex.
        // Otherwise cmdk auto-selects the first row (its keyboard handler never
        // fires here — focus stays in the editor) and leaves a second highlight
        // behind as the arrow keys move our selectedIndex.
        value={activeId}
        onValueChange={(value) => {
          const nextIndex = items.findIndex((item) => item.id === value);
          if (nextIndex >= 0) {
            setSelectedIndex(nextIndex);
          }
        }}
      >
        <CommandList className="max-h-[280px] p-1" ref={listRef}>
          {agentsLoading && mode === "agents" && (
            <div className="flex items-center justify-center py-6 text-[13px] text-muted-foreground">
              Loading agents...
            </div>
          )}

          {skillsLoading && mode === "skills" && (
            <div className="flex items-center justify-center py-6 text-[13px] text-muted-foreground">
              Loading skills...
            </div>
          )}

          {!agentsLoading && !skillsLoading && items.length === 0 && <CommandEmpty>{emptyCopy}</CommandEmpty>}

          {!agentsLoading && !skillsLoading && items.length > 0 &&
            sections.map((section, sectionIndex) => {
              if (section.items.length === 0) {
                return null;
              }
              return (
                <CommandGroup
                  key={section.heading ?? `section-${sectionIndex}`}
                  heading={section.heading}
                  className="p-0"
                >
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <CommandItem
                        className={cn(
                          "flex cursor-pointer items-start gap-2.5 rounded-md px-2.5 py-2 text-[13px] text-foreground transition-colors",
                          "aria-selected:bg-gray-100 aria-selected:text-foreground"
                        )}
                        data-active={item.id === activeId}
                        key={item.id}
                        onMouseDown={(event) => event.preventDefault()}
                        onSelect={() => handleSelect(item)}
                        value={item.id}
                      >
                        <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                        <div className="flex min-w-0 flex-col gap-0.5">
                          <span className="truncate font-medium leading-4">
                            {item.label}
                          </span>
                          <span className="truncate text-[11.5px] leading-4 text-muted-foreground">
                            {item.description}
                          </span>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              );
            })}
        </CommandList>
      </Command>
    </div>
  );

  return createPortal(dropdown, document.body);
}
